import { useState, useMemo, SyntheticEvent } from 'react';

import { useRecoilValue } from 'recoil';

import PersonIcon from '@mui/icons-material/Person';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';

import {
  assignAssociatesToReviewer,
  unassignAssociateFromReviewer,
} from 'src/api/projects';
import MultiDropdownMenu from 'src/components/Input/MultiDropDownMenu';
import useAlertSnackbar from 'src/hooks/useAlertSnackbar';
import useModality from 'src/hooks/useModality';
import { userState } from 'src/states/user';
import { SelectOption } from 'src/types/client/ui';
import { Reviewer } from 'src/types/client/user';

import CommonDialog, { CommonDialogProps } from './CommonDialog';
import {
  UserCard,
  StyledAvatar,
  PrimaryActionButton,
  DropdownSection,
} from './SharedDialogStyles';

interface Props extends CommonDialogProps {
  projectId: string;
  reviewer: Reviewer;
  onUpdate: () => void;
}

export default function UpdateAnnotatorDialog({
  open,
  onClose,
  onUpdate,
  projectId,
  reviewer,
}: Props): JSX.Element {
  const { openAlertSnackbar } = useAlertSnackbar();
  const modalityLabel = useModality();

  const allAnnotatorsInProject = useRecoilValue(
    userState.annotatorOptionList({
      projectId,
      modality: modalityLabel,
    })
  );

  const allAnnotatorsInProjectExceptCurrentReviewer = useMemo(
    () => allAnnotatorsInProject.filter(opt => opt.value !== reviewer.id),
    [allAnnotatorsInProject, reviewer]
  );

  const initialAssignedAnnotatorsForCurrentReviewer = reviewer.associates.map(
    assoc => {
      const opt = allAnnotatorsInProjectExceptCurrentReviewer.find(
        opt => opt.value === assoc.id
      );
      return { value: assoc.id, label: opt?.label || 'loading...' };
    }
  );

  const [assignedAnnotators, setAssignedAnnotators] = useState<SelectOption[]>(
    initialAssignedAnnotatorsForCurrentReviewer
  );

  const handleSelectAnnotator = (
    event: SyntheticEvent,
    value: SelectOption[] | null
  ) => {
    setAssignedAnnotators(value || []);
  };

  const handleClickAddAnnotators = async () => {
    try {
      const currentAssociateIds = assignedAnnotators.map(user => user.value);
      const previousAssociateIds = reviewer.associates.map(assoc => assoc.id);

      // Find associates to add (in current but not in previous)
      const associatesToAdd = currentAssociateIds.filter(
        id => !previousAssociateIds.includes(id)
      );

      // Find associates to remove (in previous but not in current)
      const associatesToRemove = previousAssociateIds.filter(
        id => !currentAssociateIds.includes(id)
      );

      // Assign new associates
      if (associatesToAdd.length > 0) {
        await assignAssociatesToReviewer(
          projectId,
          reviewer.id,
          associatesToAdd,
          modalityLabel
        );
      }

      // Unassign removed associates
      if (associatesToRemove.length > 0) {
        await Promise.all(
          associatesToRemove.map(associateId =>
            unassignAssociateFromReviewer(
              projectId,
              reviewer.id,
              associateId,
              modalityLabel
            )
          )
        );
      }

      openAlertSnackbar({
        severity: 'success',
        description: 'Successfully updated annotators',
      });
    } catch {
      openAlertSnackbar({
        severity: 'error',
        description: 'Failed to update annotators',
      });
    }
    onUpdate();
  };

  const handleClose = () => {
    if (onClose) onClose();
    setAssignedAnnotators(initialAssignedAnnotatorsForCurrentReviewer);
  };

  const availableCount = allAnnotatorsInProjectExceptCurrentReviewer.length;

  return (
    <CommonDialog
      onClose={handleClose}
      title="Assign Annotators"
      open={open}
      fullWidth
      maxWidth="sm"
    >
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select annotators from your project to assign to this reviewer for
          review tasks.
        </Typography>

        <UserCard variant="primary">
          <StyledAvatar variant="primary">
            <PersonIcon sx={{ fontSize: 32 }} />
          </StyledAvatar>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Reviewer
            </Typography>
            <Typography variant="h6" fontWeight={600}>
              {reviewer.username}
            </Typography>
          </Box>
        </UserCard>

        <DropdownSection>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            Available Annotators
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select from {availableCount} registered annotator
            {availableCount !== 1 ? 's' : ''} in this project
          </Typography>

          <MultiDropdownMenu
            label="Assigned annotators"
            options={allAnnotatorsInProjectExceptCurrentReviewer}
            onChange={handleSelectAnnotator}
            value={assignedAnnotators}
          />
        </DropdownSection>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" sx={{ px: 3 }}>
          Cancel
        </Button>
        <PrimaryActionButton
          onClick={handleClickAddAnnotators}
          disabled={allAnnotatorsInProjectExceptCurrentReviewer.length === 0}
          variant="contained"
          sx={{ px: 3 }}
        >
          Assign
        </PrimaryActionButton>
      </DialogActions>
    </CommonDialog>
  );
}
